import React, { useEffect, useState } from 'react';
import { Form, Field } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, RFFErrorMessage, RFFReactSelect, RFFTextInput, TextAreaInput } from '@qwealth/qcore';
import { Partner, IPartner, selectUserPartner } from '@qwealth/qdata';
import { DocumentUploader } from '@qwealth/kyc';
import { uploadFiles } from '@qwealth/qvault';

import TransitionBox from 'components/Households/Common/QNotes/TransitionBox';
import { addNewKyp, setKyps } from 'data/actions/KYP';
import { patchKyp, postKyp } from 'data/data-layer/kyp';
import { Kyp, KypTypeValues, KypAssetClassOptions, KypSelectableTypeValues, KypFormType } from 'data/models/Kyp';
import { selectKyps } from 'data/selectors/kypSelectors';

interface KypFormProps {
  kyp?: Kyp;
  onClose: () => void;
  partners: Partner[];
  formType?: KypFormType;
}

const KypForm: React.FC<KypFormProps> = ({ kyp, onClose, partners, formType }) => {
  const isEditingKyp = kyp !== undefined;
  const kyps = useSelector(selectKyps);
  const userPartner = useSelector(selectUserPartner);
  const dispatch = useDispatch();
  const [attachmentsPath, setAttachmentsPath] = useState<string>('');

  const initialValues = {
    name: '',
    type: undefined,
    issuer: '',
    ticker: '',
    partner: userPartner?.id ? userPartner.id : 99,
    approvedFor: '',
    isinCode: '',
    assessment: '',
  };

  const updateReduxWithKyp = (newKyp: Kyp) => {
    if (isEditingKyp) {
      const updatedKyps = kyps.map(product => (product.uuid === newKyp.uuid ? newKyp : product));
      dispatch(setKyps(updatedKyps));
    } else {
      dispatch(addNewKyp(newKyp));
    }
  };

  const handleSubmit = async (values: any): Promise<boolean> => {
    if (formType === 'edit') {
      values.isApproved = 0;
      values.reviewStatus = 'pending';
    }

    const newKyp = isEditingKyp ? await patchKyp(values) : await postKyp(values);
    if (!newKyp) return false;

    if (values.pending) {
      await uploadFiles(values.pending, `kyp/${newKyp.uuid}/`);
    }

    updateReduxWithKyp(newKyp);
    onClose();
    return true;
  };

  useEffect(() => {
    if (kyp) {
      const path = `kyp/${kyp.uuid}/`;
      setAttachmentsPath(path);
    }
  }, [kyp]);

  const validateKypForm = values => {
    const { name, type, assetClass, ticker, partner, isinCode, assessment, pending } = values;
    return {
      name: name.length > 0 ? undefined : 'Security Name cannot be empty',
      type: KypTypeValues.includes(type) ? undefined : 'Invalid Security Type',
      assetClass: KypAssetClassOptions.includes(assetClass) ? undefined : 'Invalid Asset Class',
      ticker: ticker.length > 0 ? undefined : 'Ticker cannot be empty',
      partner: partner >= 0 ? undefined : 'Invalid Partner',
      isinCode: isinCode.length > 0 ? undefined : 'ISM Code cannot be empty',
      assessment: assessment !== undefined && assessment.length > 0 ? undefined : 'Assessment cannot be empty',
      attachments: formType !== 'create' || (pending && pending.length > 0) ? undefined : 'Must have at least 1 attachment',
    };
  };

  return (
    <TransitionBox index={2} isVisible={true}>
      <Form<Kyp>
        validate={validateKypForm}
        onSubmit={handleSubmit}
        initialValues={kyp ? kyp : initialValues}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="space-between" marginBottom="large">
              <Box>
                <RFFTextInput
                  name="name"
                  label="Security Name*"
                  inputFieldProps={{
                    placeholder: 'Enter Security Name',
                    width: '30vh',
                  }}
                />
              </Box>

              <Box width="20vh">
                <Box textStyle="formLabel">Security Type*</Box>
                <RFFReactSelect
                  name="type"
                  inputFieldProps={{
                    options: [...KypSelectableTypeValues],
                  }}
                />
              </Box>

              <Box>
                <RFFTextInput
                  name="ticker"
                  label="Ticker*"
                  inputFieldProps={{
                    width: '20vh',
                    placeholder: 'Enter Ticker',
                  }}
                />
              </Box>
              <Box width="20vh">
                <Box textStyle="formLabel">Asset Class*</Box>
                <RFFReactSelect
                  name="assetClass"
                  inputFieldProps={{
                    options: [...KypAssetClassOptions],
                  }}
                />
              </Box>
              <Box width="20vh">
                <RFFTextInput
                  name="isinCode"
                  label="ISM Code*"
                  inputFieldProps={{
                    width: '20vh',
                    placeholder: 'Enter ISM Code',
                  }}
                />
              </Box>

              <Box width="30vh">
                <Box textStyle="formLabel">Partner*</Box>
                <RFFReactSelect
                  name="partner"
                  inputFieldProps={{
                    options: partners,
                    toValue: (item: IPartner) => item.id ?? -1,
                    toLabel: (item: IPartner) => item.partnerName,
                  }}
                />
              </Box>
            </Box>

            <Box marginTop="default">
              <Box fontWeight="bold" marginBottom="default">
                Assessment
              </Box>

              <Field name="assessment">
                {({ input, meta }) => (
                  <Box>
                    <TextAreaInput
                      rows={6}
                      value={input?.value ? input.value : ''}
                      onChange={e => input.onChange(e.target.value)}
                      width="100%"
                      onBlur={() => {
                        input.onBlur();
                      }}
                    />
                    {meta.error && meta.touched && <Box color="error">{meta.error}</Box>}
                  </Box>
                )}
              </Field>
            </Box>

            <Field name="attachments">
              {({ input, meta }) => (
                <Box>
                  <DocumentUploader
                    title="Attachments"
                    label="KYP Attachments"
                    editing={true}
                    isMulti={true}
                    directPath={attachmentsPath}
                    documentPreviewPosition="side"
                  />
                  {meta.error && meta.touched && <Box color="error">{meta.error}</Box>}
                </Box>
              )}
            </Field>

            <Box display="flex" flexDirection="row" width="100%" justifyContent="right" marginTop="large" gap="default">
              <Button type="cancel" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">{isEditingKyp ? 'Save' : 'Create'}</Button>
            </Box>
          </form>
        )}
      />
    </TransitionBox>
  );
};

export default KypForm;
